const FILTER_STORAGE_KEY = 'database-schema-filters'

export interface DatabaseFilterSettings {
  hiddenDatabases: string[]
  hiddenSchemas: Record<string, string[]>
}

const DEFAULT_SYSTEM_DATABASES = {
  mysql: ['information_schema', 'mysql', 'performance_schema', 'sys'],
  postgresql: ['pg_catalog', 'pg_temp_1', 'pg_toast']
}

const DEFAULT_SYSTEM_SCHEMAS = {
  mysql: [],
  postgresql: ['information_schema', 'pg_catalog', 'pg_toast', 'pg_temp', 'pg_toast_temp']  // Removed 'public' as it's often used
}

export function getFilterSettings(datasourceId: string, databaseType: 'mysql' | 'postgresql'): DatabaseFilterSettings {
  try {
    const stored = localStorage.getItem(FILTER_STORAGE_KEY)
    if (!stored) {
      const defaults = getDefaultSettings(databaseType)
      // Save the defaults so they persist
      saveFilterSettings(datasourceId, defaults)
      return defaults
    }
    
    const parsed = JSON.parse(stored) as unknown
    const allSettings: Record<string, unknown> = (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed as Record<string, unknown> : {}
    
    if (!allSettings[datasourceId]) {
      const defaults = getDefaultSettings(databaseType)
      // Save the defaults for this datasource
      saveFilterSettings(datasourceId, defaults)
      return defaults
    }
    
    const dsValue = allSettings[datasourceId]
    if (dsValue && typeof dsValue === 'object') {
      const candidate = dsValue as Partial<DatabaseFilterSettings>
      const hs = candidate.hiddenSchemas
      const hiddenSchemas: Record<string, string[]> = {}
      if (hs && typeof hs === 'object') {
        for (const [db, schemas] of Object.entries(hs as Record<string, unknown>)) {
          hiddenSchemas[db] = Array.isArray(schemas) ? (schemas as unknown[]).filter((x): x is string => typeof x === 'string') : []
        }
      }
      return {
        hiddenDatabases: Array.isArray(candidate.hiddenDatabases) ? candidate.hiddenDatabases.filter((x): x is string => typeof x === 'string') : [],
        hiddenSchemas
      }
    }
    return getDefaultSettings(databaseType)
  } catch {
    const defaults = getDefaultSettings(databaseType)
    saveFilterSettings(datasourceId, defaults)
    return defaults
  }
}

export function saveFilterSettings(datasourceId: string, settings: DatabaseFilterSettings): void {
  try {
    const stored = localStorage.getItem(FILTER_STORAGE_KEY)
    const parsed = stored ? (JSON.parse(stored) as unknown) : undefined
    const allSettings: Record<string, unknown> = (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed as Record<string, unknown> : {}
    
    allSettings[datasourceId] = settings
    
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(allSettings))
  } catch (error) {
    console.error('Failed to save filter settings:', error)
  }
}

export function resetFilterSettings(datasourceId: string, databaseType: 'mysql' | 'postgresql'): void {
  const defaultSettings = getDefaultSettings(databaseType)
  saveFilterSettings(datasourceId, defaultSettings)
}

function getDefaultSettings(databaseType: 'mysql' | 'postgresql'): DatabaseFilterSettings {
  const settings: DatabaseFilterSettings = {
    hiddenDatabases: DEFAULT_SYSTEM_DATABASES[databaseType] || [],
    hiddenSchemas: {}
  }
  
  // For PostgreSQL, apply system schemas as global filters (apply to all databases)
  if (databaseType === 'postgresql') {
    settings.hiddenSchemas['*'] = DEFAULT_SYSTEM_SCHEMAS[databaseType] || []
  }
  
  return settings
}

function matchesHidden(schemaName: string, list: string[]): boolean {
  if (list.includes(schemaName)) return true
  // Also hide schemas that start with a hidden prefix + underscore, e.g., pg_temp_* or pg_toast_temp_*
  for (const entry of list) {
    if (typeof entry === 'string' && entry.length > 0) {
      if (schemaName.startsWith(`${entry}_`)) return true
    }
  }
  return false
}

export function isDatabaseHidden(datasourceId: string, databaseName: string, databaseType: 'mysql' | 'postgresql'): boolean {
  const settings = getFilterSettings(datasourceId, databaseType)
  return settings.hiddenDatabases.includes(databaseName)
}

export function isSchemaHidden(datasourceId: string, databaseName: string, schemaName: string, databaseType: 'mysql' | 'postgresql'): boolean {
  const settings = getFilterSettings(datasourceId, databaseType)
  const dbSchemas = settings.hiddenSchemas[databaseName] || []
  const globalSchemas = settings.hiddenSchemas['*'] || []
  return matchesHidden(schemaName, dbSchemas) || matchesHidden(schemaName, globalSchemas)
}